import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  fetchInspectionReview,
  saveInspectionTour,
  type InspectionCapture,
  type InspectionTour,
  type InspectionTourRequest,
} from '../api/inspections';
import { fetchSession, type SessionFull } from '../api/missions';
import { ca } from '../i18n/ca';
import { useCanReview } from './reviewAccess';
import { buildReviewItems, groupCaptures } from './inspectionGroups';
import { PhotoViewer } from './PhotoViewer';
import { ReviewToolbar } from './ReviewToolbar';
import { Shell } from './Shell';
import { errorText } from './useSessionLive';
import { TourChapter } from './TourChapter';
import { useCaptureSave } from './useCaptureSave';
import { useReviewFlow } from './useReviewFlow';

/**
 * The review page for one session's captures ({@code /proves/inspeccio/:code}):
 * a closeup inspection tour's, or the loose ones of any other session (the
 * reports of a guided test, a person's own hotkey captures). The mission's
 * own facts at the top, from the session's definition, the tour as a whole
 * (where it stands, the comment, the verdict), then every capture in order,
 * each marked on its own card and openable full-screen to zoom. The
 * facts-and-grid part is `TourChapter`, shared with the campaign's own
 * one-page review (`InspectionCampaignReview.tsx`), which repeats it once per
 * tour.
 */
export function InspectionReview() {
  const { code } = useParams<{ code: string }>();
  const t = ca.inspectionReviewPage;
  const canReview = useCanReview();
  const [session, setSession] = useState<SessionFull | null>(null);
  const [captures, setCaptures] = useState<InspectionCapture[] | null>(null);
  const [campaignId, setCampaignId] = useState<number | null>(null);
  const [tour, setTour] = useState<InspectionTour | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [autoFindings, setAutoFindings] = useState<string[]>([]);

  useEffect(() => {
    if (!code) return;
    fetchSession(code, 'direction')
      .then(setSession)
      .catch(() => setSession(null));
    fetchInspectionReview(code)
      .then((view) => {
        setCaptures(view.captures);
        setAutoFindings(view.autoFindings ?? []);
        setCampaignId(view.campaignId ?? null);
        setTour(view.tour ?? null);
      })
      .catch((e) => setError(errorText(e)));
  }, [code]);

  const patch = useCallback((uploadId: number, change: Partial<InspectionCapture>) => {
    setCaptures((all) =>
      (all ?? []).map((c) => (c.uploadId === uploadId ? { ...c, ...change } : c)),
    );
  }, []);
  const { save, savingId } = useCaptureSave(patch, setError, canReview);

  const saveTour = useCallback(
    (body: InspectionTourRequest) => {
      if (!code || !canReview) return;
      saveInspectionTour(code, body)
        .then(setTour)
        .catch((e) => setError(errorText(e)));
    },
    [code, canReview],
  );

  const items = useMemo(
    () => buildReviewItems(session?.definition, captures ?? []),
    [session, captures],
  );
  const flow = useReviewFlow(items, save);

  const markGroupCorrect = useCallback(
    (groupKey: string) => {
      const group = groupCaptures(captures ?? []).find((g) => g.key === groupKey);
      if (!group) return;
      for (const c of group.captures) {
        if (!c.flags.includes('correct')) save(c.uploadId, [...c.flags, 'correct'], c.note ?? '');
      }
    },
    [captures, save],
  );

  if (!code) return null;
  const all = captures ?? [];

  return (
    <Shell title={t.title(session?.missionTitle ?? code)} width="wide">
      <p>
        <Link to="/proves">← {ca.testsPage.back}</Link>
        {campaignId != null && (
          <>
            {' · '}
            <Link to={`/proves/inspeccions/campanya/${campaignId}/revisio#chapter-${code}`}>
              {t.campaignReview}
            </Link>
          </>
        )}
        {' · '}
        <Link to="/proves/inspeccions">{t.back}</Link>
      </p>
      <ReviewToolbar
        total={all.length}
        reviewed={all.filter((c) => c.reviewedBy).length}
        problems={all.filter((c) => c.flags.includes('problem')).length}
        filter={flow.filter}
        onFilter={flow.setFilter}
        onQueue={flow.openQueue}
      />
      <TourChapter
        code={code}
        session={session}
        captures={captures}
        items={items}
        autoFindings={autoFindings}
        error={error}
        gridIndex={flow.gridIndex}
        viewerOpen={flow.viewerIndex !== null}
        onOpenViewer={flow.setViewerIndex}
        onFocusIndex={flow.setFocusedIndex}
        onMarkGroupCorrect={markGroupCorrect}
        onToggleFlag={flow.toggleFlag}
        onNote={flow.saveNote}
        savingId={savingId}
        filter={flow.filter}
        tour={tour}
        onTour={saveTour}
      />
      {flow.viewerIndex !== null && (
        <PhotoViewer
          items={items}
          index={flow.viewerIndex}
          onClose={() => flow.setViewerIndex(null)}
          onIndex={flow.setViewerIndex}
          onToggleFlag={flow.toggleFlag}
          onNote={flow.saveNote}
          advance={flow.advance}
          onAdvance={flow.setAdvance}
          onModerated={(uploadId, outcome) => {
            if (outcome === 'deleted') flow.setViewerIndex(null);
            setCaptures((list) =>
              outcome === 'deleted'
                ? (list ?? []).filter((c) => c.uploadId !== uploadId)
                : (list ?? []).map((c) =>
                    c.uploadId === uploadId ? { ...c, withheld: false } : c,
                  ),
            );
          }}
          savingNote={(() => {
            const at = items[flow.viewerIndex];
            return at?.type === 'capture' ? savingId === at.capture.uploadId : false;
          })()}
        />
      )}
    </Shell>
  );
}
